import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  EventEmitter,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { TwampReflectorSetupComponent } from './components/twamp-reflector-setup/twamp-reflector-setup.component';
import { ActivatedRoute, Router } from '@angular/router';
import { TwampReflectorTestService } from './twamp-reflector-test.service';
import { takeUntil } from 'rxjs/operators';
import {
  DevicesService,
  DialogService,
  NotificationService,
  TestListService,
  TestTypeValue,
  ValuesExchangeProvider
} from '@nrwl/common-lib';
import {
  BaseSetupTab,
  ControllerApiService,
  ITestSetupEntry,
  SaveTemplateComponent,
  TemplateService,
  TestSetupComponent,
  TestSetupTabsComponent
} from '@nrwl/test-setup';
import { NavigationProvider } from '@nrwl/navigation-abstract';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subject, Subscription } from 'rxjs';
import { TemplateTabsComponent } from '@nrwl/template-management';
import { get, merge } from 'lodash';
import { TwampReflectorConfigMapping as ConfigFieldMapping } from './model/twamp-reflector-agent-model';

@Component({
  selector: 'nrwl-twamp-reflector-test',
  providers: [TwampReflectorTestService],
  template: '<nrwl-test-setup-tabs [parentObj]="currentObj"></nrwl-test-setup-tabs>'
})
export class TwampReflectorTestComponent
  extends TestSetupComponent
  implements OnInit, AfterViewInit, OnDestroy, ITestSetupEntry
{
  @ViewChild(TestSetupTabsComponent, { static: true })
  testSetupTabRef: TestSetupTabsComponent;
  editDeviceDialogRef: MatDialogRef<SaveTemplateComponent>;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  setupConfig: any;
  setupId: string;
  testId: string;
  mode: string;
  agentBasedConfig: any;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClose = new EventEmitter<any>();
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  dataId: any;
  router: Router;
  setupService: TwampReflectorTestService;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  openWizard: any;
  editMode: boolean;
  subscription: Subscription;
  changeDetectorRef: ChangeDetectorRef;
  activatedRouteSubscription: Subscription;
  templateDialogRef: MatDialogRef<TemplateTabsComponent>;
  currentObj: any; //eslint-disable-line
  templateId: string;
  private readonly unsubscribe: Subject<void> = new Subject();
  constructor(
    router: Router,
    setupService: TwampReflectorTestService,
    changeDetectorRef: ChangeDetectorRef,
    private readonly exchange: ValuesExchangeProvider,
    private readonly activatedRoute: ActivatedRoute,
    private readonly factoryResolver: ComponentFactoryResolver,
    private readonly templateService: TemplateService,
    private readonly notificationService: NotificationService,
    private readonly testListService: TestListService,
    private readonly navigationProvider: NavigationProvider,
    private dialog: MatDialog,
    private readonly dialogService: DialogService,
    private readonly controllerApiService: ControllerApiService,
    private readonly deviceService: DevicesService
  ) {
    super();
    this.router = router;
    this.setupService = setupService;
    this.changeDetectorRef = changeDetectorRef;
  }

  ngOnInit() {
    this.currentObj = this;
    this.activatedRouteSubscription = this.activatedRoute.queryParams.subscribe(data => {
      if (this.testId !== this.setupId) {
        const defaultParams = merge(
          {},
          this.dataId,
          { modeType: this.mode },
          JSON.parse(get(data, 'defaultParams', '{}'))
        );
        this.templateId = defaultParams?.templateId;
        this.setupService.openTestSetup(
          this.setupId,
          this.setupConfig,
          defaultParams,
          null,
          null,
          this.agentBasedConfig
        );
        this.testId = this.setupId;
      }
    });
    this.exchange.notifyListeners('new_test', { noValue: true });
    this.templateService.closeDialog?.subscribe(status => {
      if (status && this.templateDialogRef) {
        this.templateDialogRef.close();
      }
    });
    //MV-25672	- update device cache
    this.deviceService.updateCache();
  }

  ngAfterViewInit() {
    this.createContentComponent(1);
    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.activatedRouteSubscription) {
      this.activatedRouteSubscription.unsubscribe();
    }
    if (this.unsubscribe) {
      this.unsubscribe.unsubscribe();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  get(object: any, path: string): any {
    return get(object, path);
  }

  //-------------- Control && Validation -----------------------
  toggleTabChange(index: number): void {
    this.setupService.toggleTabChange(index);
    const tabIndex = index + 1;
    // create the component only once when its not available
    if (!this.testSetupTabRef['contentRef' + `${tabIndex}`].length) {
      this.createContentComponent(tabIndex);
    }
  }

  toggleClose(): void {
    this.setupService.toggleClose(() => this.onClose.emit(true));
  }

  toggleReOpen(): void {
    this.setupService.reOpen();
  }

  toggleFinish(): void {
    this.setupService.onNext();
    this.editMode = this.setupService.editMode;
    this.createTwampTest();
  }
  createTwampTest(): void {
    const twampLmapObject = this.setupService.createLMAPObject();
    const message = this.editMode ? 'Updating test' : 'Creating test';
    this.setupService.openTestStatusDialog(message, this.editMode ? 'Update' : 'Add');
    const isJMEPAgent = this.setupService.output[ConfigFieldMapping.AGENT_TYPE].includes('JMEP');
    if (!this.setupService.editMode) {
      const saveControllerApi = isJMEPAgent
        ? this.controllerApiService.setJMEPConfig({ reqData: twampLmapObject })
        : this.setupService._saveControllerTest(twampLmapObject);
      saveControllerApi.pipe(takeUntil(this.unsubscribe)).subscribe(
        response => {
          const message = 'Twamp Reflector test has been created';
          this.notificationService.onSuccess(message);
          this.testListService.updateTestResponse();
          this.setupService.dialogRef.close();
          this.toggleClose();
          this.exchange.notifyListeners('new_test', response);
          this.goToTestListPage(response.data.id);
        },
        error => {
          const message = 'Failed to create twamp reflector test';
          this.notificationService.onFail(message);
          this.setupService.dialogRef.close();
          this.toggleClose();
        }
      );
    } else {
      const updateControllerApi = isJMEPAgent
        ? this.controllerApiService.setJMEPConfig({ reqData: twampLmapObject })
        : this.setupService._updateControllerTest({
            opt: { id: this.setupService.defaultParamsById['scheduleId'], apply: 'next-run' },
            config: twampLmapObject
          });
      updateControllerApi.pipe(takeUntil(this.unsubscribe)).subscribe(
        response => {
          const message = 'Twamp Reflector test has been updated';
          this.notificationService.onSuccess(message);
          this.testListService.updateTestResponse();
          this.setupService.dialogRef.close();
          this.toggleClose();
          this.exchange.notifyListeners('new_test', response);
        },
        error => {
          const message = 'Failed to update twamp reflector test';
          this.notificationService.onFail(message);
          this.setupService.dialogRef.close();
          this.toggleClose();
        }
      );
    }
  }

  goToTestListPage(testId: string): void {
    this.navigationProvider.navigateToDashboard(['test-list-management'], {
      queryParams: {
        hideTestSetup: true,
        queryParamsHandling: 'merge',
        replaceUrl: 'true'
      }
    });
  }

  closeSetupPage(): void {
    this.setupService.toggleClose(() => this.onClose.emit(true));
    this.setupService.updateBulkTestStatusDialog(
      this.editMode ? 'Twamp test has been updated' : 'Twamp test has been created',
      '',
      true
    );
  }

  downloadTestConfig(): void {
    this.setupService.onNext();
    const lmapObject = this.setupService.createLMAPObject();
    this.setupService._downloadTestConfig(lmapObject);
  }

  saveTestConfig(): void {
    this.setupService.onNext();
    const configParams = this.setupService.getTemplateConfig();
    const allTestParams = this.setupService._createTemplateFormat(
      configParams,
      this.setupService.output.selectedOriginator,
      null
    );
    this.editDeviceDialogRef = this.dialog.open(SaveTemplateComponent, {
      width: '70rem',
      disableClose: true,
      data: {
        deviceInfo: allTestParams,
        editMode: true,
        testType:
          this.setupService.output[ConfigFieldMapping.REFLECTOR_MODE] === 'light'
            ? TestTypeValue.TWAMP_LIGHT_REFLECTOR
            : TestTypeValue.TWAMP_REFLECTOR
      }
    });
  }

  openTemplates(): void {
    const param = {
      id: 'setupService.test_templates',
      callBackService: this,
      callBackFuntion: this.openTemplatesDialog
    };
    this.dialogService.showDialog('confirm', 'body', param);
  }

  //eslint-disable-next-line
  openTemplatesDialog(service: any): void {
    service.toggleClose();
    service.templateDialogRef = service.dialog.open(TemplateTabsComponent, {
      width: '100rem',
      minHeight: '50rem',
      height: 'calc(100vh - 20px)',
      disableClose: true,
      data: {
        filterType: TestTypeValue.TWAMP_REFLECTOR,
        myTemplatesColumnHeaders: ['name', 'accessLevel', 'lastModified', 'selectActions'],
        sharedTemplatesColumnHeaders: [
          'name',
          'accessLevel',
          'owner',
          'lastModified',
          'selectActions'
        ]
      }
    });
  }

  getSetupService(): TwampReflectorTestService {
    return this.setupService;
  }
  getSetupTab(): BaseSetupTab {
    return this.setupService.currentTab;
  }

  getFactoryResolver(): ComponentFactoryResolver {
    return this.factoryResolver;
  }

  getContentViewRef(index: number): ViewContainerRef {
    return this.testSetupTabRef['contentRef' + `${index}`];
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  resolveComponentType(pageId: string): any {
    switch (pageId) {
      case 'twamp-reflector-test.component.html':
        return TwampReflectorSetupComponent;

      default: {
        console.error('Unrecognized template name:', pageId);
        return null;
      }
    }
  }
}
